import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  DocumentHeader,
  DocumentSection,
  DocumentInfoGrid,
  DocumentTable,
  DocumentSignatureBlock,
  DocumentFooter,
  DocumentModalShell,
} from './DocumentBranding';
import { downloadElementAsPdf } from '../../../utils/documentPdfDownload';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
const fmtMoney = (n) =>
  n === null || n === undefined || n === '' ? '—' : `₦${Number(n).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;

const PurchaseOrderDocumentView = ({ po, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  if (!po) return null;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await downloadElementAsPdf('procurement-document-content', `Purchase_Order_${po.po_number}.pdf`);
    } catch (error) {
      console.error('Error generating Purchase Order PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const items = po.items || [];
  const itemRows = items.map((it, i) => [
    i + 1,
    it.description || '',
    it.units || '',
    it.qty || '',
    it.unit || '',
    fmtMoney(it.unit_price),
    fmtMoney(it.line_total),
  ]);

  return (
    <DocumentModalShell onDownload={handleDownload} onClose={onClose} downloading={downloading}>
      <DocumentHeader
        title="PURCHASE ORDER"
        subtitle={`PO No. ${po.po_number}`}
        badgeLabel="PURCHASE ORDER"
        badgeColor="bg-blue-600"
      />

      <div className="bg-blue-50 border-b border-blue-200 px-10 py-4">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">PO Number</p>
            <p className="text-xl font-bold text-blue-900">{po.po_number}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">PO Date</p>
            <p className="text-lg font-semibold text-gray-900">{fmtDate(po.po_date)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Delivery Date Required</p>
            <p className="text-lg font-semibold text-gray-900">{fmtDate(po.delivery_date_required)}</p>
          </div>
        </div>
      </div>

      <DocumentSection title="SUPPLIER / VENDOR">
        <DocumentInfoGrid
          pairs={[
            ['Vendor Company Name', po.supplier_company_name],
            ['Contact Person', po.supplier_contact],
            ['Address', po.supplier_address],
          ]}
        />
      </DocumentSection>

      <DocumentSection title="SHIP TO">
        <DocumentInfoGrid
          pairs={[
            ['Ship To Company', po.ship_to_company],
            ['Delivery Address', po.ship_to_address],
            ['Receiving Contact', po.ship_to_contact],
          ]}
        />
      </DocumentSection>

      <DocumentSection title="ORDER INFORMATION">
        <DocumentInfoGrid
          pairs={[
            ['Requisition / RFQ Ref.', po.requisition_ref],
            ['Buyer Name', po.buyer_name],
            ['Shipping Method', po.shipping_method],
            ['Payment Terms', po.payment_terms],
            ['Currency', po.currency],
          ]}
        />
      </DocumentSection>

      <DocumentSection title="ORDER DETAILS">
        <DocumentTable
          headers={['#', 'Item Description', 'Units', 'Qty', 'Unit', 'Unit Price', 'Line Total']}
          rows={itemRows}
          alignRight={[5, 6]}
        />
        <div className="mt-4 flex justify-end">
          <div className="w-64 space-y-1">
            <div className="flex justify-between text-sm text-gray-700">
              <span>Subtotal</span>
              <span className="font-medium">{fmtMoney(po.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-700">
              <span>Tax</span>
              <span className="font-medium">{fmtMoney(po.tax)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-700">
              <span>Shipping</span>
              <span className="font-medium">{fmtMoney(po.shipping)}</span>
            </div>
            <div className="flex justify-between bg-green-50 px-3 py-2 rounded-lg border-2 border-green-600 mt-2">
              <span className="text-sm font-bold text-green-800">TOTAL</span>
              <span className="text-lg font-bold text-green-700">{fmtMoney(po.total)}</span>
            </div>
          </div>
        </div>
      </DocumentSection>

      {po.terms_conditions && (
        <DocumentSection title="TERMS & CONDITIONS">
          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{po.terms_conditions}</p>
        </DocumentSection>
      )}

      <DocumentSection title="AUTHORIZATION">
        <DocumentSignatureBlock labels={['Authorized Signatory (Buyer)', 'Vendor Acknowledgement']} />
      </DocumentSection>

      <DocumentFooter documentId={po.po_number} />
    </DocumentModalShell>
  );
};

export default PurchaseOrderDocumentView;
